import { useEffect, useState } from "react";
import axios from "axios";
import { X, CheckCircle2, LoaderCircle } from "lucide-react";
import { track } from "@/services/metrics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", company: "", service: "General", message: "", website: "" };

export const QuoteForm = ({ open, onClose, defaultService = "General" }) => {
  const [form, setForm] = useState({ ...initial, service: defaultService });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, onClose]);

  if (!open) return null;
  const update = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const submit = async (event) => {
    event.preventDefault();
    setState("loading"); setError("");
    try {
      await axios.post(`${API}/inquiries`, { ...form, service: form.service || defaultService }, { timeout: 12000 });
      track("quote_submit");
      setState("success");
    } catch (err) {
      setState("error");
      setError(err.response?.data?.detail?.[0]?.msg || "We couldn't send your request. Please try again or contact us directly.");
    }
  };

  return (
    <div className="modal-backdrop" data-testid="quote-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="quote-modal" role="dialog" aria-modal="true" aria-labelledby="quote-title" data-testid="quote-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close quote form" data-testid="quote-modal-close-button"><X /></button>
        {state === "success" ? (
          <div className="success-state" data-testid="quote-success-message">
            <CheckCircle2 size={48} />
            <p className="eyebrow">Request received</p>
            <h2 id="quote-title">WE'RE ON IT.</h2>
            <p>Thanks, {form.name}. Our team will contact you shortly to discuss the brief.</p>
            <button className="button button-primary" onClick={() => { setForm({ ...initial, service: defaultService }); setState("idle"); onClose(); }} data-testid="quote-success-close-button">Back to site</button>
          </div>
        ) : (
          <>
            <p className="eyebrow" data-testid="quote-form-eyebrow">Start a conversation</p>
            <h2 id="quote-title" data-testid="quote-form-title">REQUEST A QUOTE</h2>
            <p className="modal-intro" data-testid="quote-form-description">Tell us what you're building. We'll bring the right technical or creative team.</p>
            <form onSubmit={submit} data-testid="quote-form">
              <div className="form-honeypot" aria-hidden="true">
                <label>Website<input name="website" value={form.website} onChange={update} tabIndex="-1" autoComplete="off" data-testid="quote-website-honeypot" /></label>
              </div>
              <div className="form-grid">
                <label>Name<input required minLength="2" maxLength="100" name="name" value={form.name} onChange={update} placeholder="Your name" autoComplete="name" data-testid="quote-name-input" /></label>
                <label>Work email<input required type="email" maxLength="254" name="email" value={form.email} onChange={update} placeholder="you@company.com" autoComplete="email" data-testid="quote-email-input" /></label>
                <label>Phone<input required minLength="7" maxLength="30" name="phone" value={form.phone} onChange={update} placeholder="+91" inputMode="tel" autoComplete="tel" data-testid="quote-phone-input" /></label>
                <label>Company<input maxLength="120" name="company" value={form.company} onChange={update} placeholder="Company / team" autoComplete="organization" data-testid="quote-company-input" /></label>
              </div>
              <label>What do you need?
                <select name="service" value={form.service || defaultService} onChange={update} data-testid="quote-service-select">
                  {["Production", "Automotive R&D", "Motorsports Management", "Gear & Apparel", "General"].map(item => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label>Project brief<textarea required minLength="10" maxLength="2000" name="message" value={form.message} onChange={update} placeholder="Timeline, scope, location, goals..." data-testid="quote-message-input" /></label>
              {error && <p className="form-error" data-testid="quote-form-error">{error}</p>}
              <button className="button button-primary form-submit" disabled={state === "loading"} data-testid="quote-form-submit-button">
                {state === "loading" ? <><LoaderCircle className="spin" size={18} /> Sending</> : "Send request"}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
};
